import { Check, ClipboardCheck, Copy, MessageCircle } from "lucide-react";
import { useDataProvider, useGetIdentity, useNotify } from "ra-core";
import { useEffect, useState } from "react";

import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";

import { getSupabaseClient } from "../providers/supabase/supabase";
import { formatDate } from "./gulaConstants";
import type { GulaClient } from "./gulaTypes";
import {
  ELEVATOR_ITEM,
  REQUIREMENT_ITEMS,
  REQUIREMENTS_STATUS_LABEL,
  buildRequirementsMessage,
  requirementsStatus,
  whatsappUrl,
  type RequirementKey,
  type RequirementValue,
} from "./requirements";

type Values = Record<RequirementKey, string | null>;

const valuesOf = (client: GulaClient): Values => ({
  req_photos: client.req_photos ?? null,
  req_drain: client.req_drain ?? null,
  req_space: client.req_space ?? null,
  req_power: client.req_power ?? null,
});

const STATUS_STYLE = {
  pending: "bg-muted text-muted-foreground",
  to_review:
    "bg-amber-100 text-amber-900 dark:bg-amber-400/15 dark:text-amber-200",
  approved:
    "bg-emerald-100 text-emerald-800 dark:bg-emerald-400/15 dark:text-emerald-200",
  not_standard: "bg-destructive/10 text-destructive",
};

const OPTION_STYLE: Record<RequirementValue, string> = {
  ok: "border-emerald-600 bg-emerald-50 text-emerald-800 dark:border-emerald-500 dark:bg-emerald-400/15 dark:text-emerald-200",
  client:
    "border-amber-500 bg-amber-50 text-amber-900 dark:bg-amber-400/15 dark:text-amber-200",
  ko: "border-destructive bg-destructive/10 text-destructive",
};

/**
 * Checklist the setter fills in the sales call (or the welcome call of a direct web
 * purchase): kitchen photos, drain, room under the sink and power socket. Each answer
 * is saved on click; then someone reviews it and approves the standard installation.
 */
export const ClientRequirements = ({
  client,
  onChange,
}: {
  client: GulaClient;
  onChange: () => void;
}) => {
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const { identity } = useGetIdentity();
  const [values, setValues] = useState<Values>(() => valuesOf(client));
  const [notes, setNotes] = useState(client.req_notes ?? "");
  const [elevator, setElevator] = useState(client.req_elevator ?? null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setValues(valuesOf(client));
    setNotes(client.req_notes ?? "");
    setElevator(client.req_elevator ?? null);
  }, [client]);

  // Any change cancels the approval in the database: mirror it while it reloads
  const status = requirementsStatus({
    ...values,
    req_approved_at: client.req_approved_at,
  });

  const save = async (data: Partial<GulaClient>) => {
    try {
      await dataProvider.update("gula_clients", {
        id: client.id,
        data,
        previousData: { id: client.id },
      });
      onChange();
    } catch (error) {
      console.error(error);
      notify("No se ha podido guardar", { type: "error" });
      setValues(valuesOf(client));
      setElevator(client.req_elevator ?? null);
    }
  };

  const answer = (key: RequirementKey, value: RequirementValue) => {
    const next = values[key] === value ? null : value;
    setValues((previous) => ({ ...previous, [key]: next }));
    save({ [key]: next });
  };

  const answerElevator = (value: string) => {
    const next = elevator === value ? null : value;
    setElevator(next);
    save({ req_elevator: next });
  };

  const approve = async (approve: boolean) => {
    setBusy(true);
    const { error } = await getSupabaseClient().rpc(
      "gula_approve_requirements",
      { p_client_id: Number(client.id), p_approve: approve },
    );
    setBusy(false);
    if (error) {
      notify(error.message, { type: "error" });
      return;
    }
    notify(approve ? "Instalación estándar aprobada" : "Aprobación quitada", {
      type: "success",
    });
    onChange();
  };

  const agentFirstName =
    typeof identity?.fullName === "string"
      ? identity.fullName.split(" ")[0]
      : null;
  const message = buildRequirementsMessage(client.first_name, agentFirstName);
  const whatsapp = client.phone ? whatsappUrl(client.phone, message) : null;
  const copyMessage = async () => {
    await navigator.clipboard.writeText(message);
    notify("Mensaje copiado", { type: "info" });
  };

  return (
    <section className="flex flex-col gap-3 rounded-lg border p-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h4 className="flex items-center gap-2 text-sm font-semibold">
          <ClipboardCheck className="h-4 w-4" />
          Requisitos de instalación estándar
        </h4>
        <span
          className={cn(
            "rounded-full px-2 py-0.5 text-xs font-medium",
            STATUS_STYLE[status],
          )}
        >
          {REQUIREMENTS_STATUS_LABEL[status]}
        </span>
      </div>
      <p className="text-xs text-muted-foreground">
        Confírmalos en la llamada de venta o, si ha comprado directamente en la
        web, en la llamada de bienvenida. La instalación solo está incluida en
        el precio si se cumplen todos.
      </p>

      <div className="flex flex-wrap gap-2">
        {whatsapp ? (
          <Button size="sm" variant="outline" asChild>
            <a href={whatsapp} target="_blank" rel="noreferrer">
              <MessageCircle className="h-4 w-4" />
              Pedir por WhatsApp
            </a>
          </Button>
        ) : null}
        <Button size="sm" variant="ghost" onClick={copyMessage}>
          <Copy className="h-4 w-4" />
          Copiar mensaje
        </Button>
      </div>

      <ul className="flex flex-col gap-3">
        {REQUIREMENT_ITEMS.map((item) => (
          <li key={item.key} className="flex flex-col gap-1.5">
            <div className="flex flex-col">
              <span className="text-sm font-medium">
                {item.label}
                {item.key === "req_photos" ? (
                  <span className="font-normal text-muted-foreground">
                    {" "}
                    · {client.nb_photo_notes ?? 0}{" "}
                    {client.nb_photo_notes === 1
                      ? "nota con fotos"
                      : "notas con fotos"}{" "}
                    (abajo)
                  </span>
                ) : null}
              </span>
              <span className="text-xs text-muted-foreground">{item.hint}</span>
            </div>
            <div
              className="flex flex-wrap gap-1.5"
              role="radiogroup"
              aria-label={item.label}
            >
              {item.options.map((option) => {
                const selected = values[item.key] === option.value;
                return (
                  <button
                    key={option.value}
                    type="button"
                    role="radio"
                    aria-checked={selected}
                    onClick={() => answer(item.key, option.value)}
                    className={cn(
                      "inline-flex items-center gap-1 rounded-md border px-2.5 py-1 text-xs transition-colors",
                      selected
                        ? OPTION_STYLE[option.value]
                        : "text-muted-foreground hover:bg-muted",
                    )}
                  >
                    {selected ? <Check className="h-3 w-3" /> : null}
                    {option.label}
                  </button>
                );
              })}
            </div>
          </li>
        ))}
      </ul>

      <div className="flex flex-col gap-1.5 border-t pt-3">
        <div className="flex flex-col">
          <span className="text-sm font-medium">{ELEVATOR_ITEM.label}</span>
          <span className="text-xs text-muted-foreground">
            {ELEVATOR_ITEM.hint}
          </span>
        </div>
        <div
          className="flex flex-wrap gap-1.5"
          role="radiogroup"
          aria-label={ELEVATOR_ITEM.label}
        >
          {ELEVATOR_ITEM.options.map((option) => {
            const selected = elevator === option.value;
            return (
              <button
                key={option.value}
                type="button"
                role="radio"
                aria-checked={selected}
                onClick={() => answerElevator(option.value)}
                className={cn(
                  "inline-flex items-center gap-1 rounded-md border px-2.5 py-1 text-xs transition-colors",
                  selected
                    ? "border-sky-600 bg-sky-50 text-sky-800 dark:border-sky-500 dark:bg-sky-400/15 dark:text-sky-200"
                    : "text-muted-foreground hover:bg-muted",
                )}
              >
                {selected ? <Check className="h-3 w-3" /> : null}
                {option.label}
              </button>
            );
          })}
        </div>
      </div>

      <Textarea
        placeholder="Observaciones para el técnico (p. ej. el enchufe lo pone su electricista el jueves)"
        value={notes}
        rows={2}
        onChange={(event) => setNotes(event.target.value)}
        onBlur={() =>
          notes.trim() !== (client.req_notes ?? "").trim() &&
          save({ req_notes: notes.trim() || null })
        }
      />

      {status === "not_standard" ? (
        <p className="rounded-md bg-destructive/10 p-2 text-xs text-destructive">
          No es una instalación estándar: el precio no la cubre. Hay que
          presupuestar el extra o que el cliente lo resuelva antes, y avisar al
          instalador.
        </p>
      ) : null}

      {status === "approved" ? (
        <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-emerald-800">
          <span>
            Revisado y aprobado
            {client.req_approved_by_name
              ? ` por ${client.req_approved_by_name}`
              : ""}{" "}
            el {formatDate(client.req_approved_at)}
          </span>
          <button
            type="button"
            disabled={busy}
            onClick={() => approve(false)}
            className="text-muted-foreground underline hover:text-foreground"
          >
            Quitar aprobación
          </button>
        </div>
      ) : (
        <Button
          size="sm"
          disabled={busy || status !== "to_review"}
          onClick={() => approve(true)}
          title={
            status === "to_review"
              ? undefined
              : "Primero hay que confirmar los cuatro requisitos"
          }
        >
          <ClipboardCheck className="h-4 w-4" />
          Revisado: aprobar instalación estándar
        </Button>
      )}
    </section>
  );
};
